import { Injectable } from "@angular/core";
import { BaseApi } from "./base/base.api";
import { firstValueFrom } from "rxjs";
import { Filter } from "@/models/filter.model";
import { HttpHeaders, HttpParams } from "@angular/common/http";
import { Pagination } from "@/models/pagination.model";
import { ExclusivePromoterProposal } from "@/models/exclusive-promoter-proposal.model";
import { ProposalOrderReduced } from "@/models/proposal-order-reduced.model";

@Injectable({
  providedIn: "root",
})
export class ExclusivePromoterProposalApi extends BaseApi {

  async proposals(filter: Filter, searchBy?: any): Promise<Pagination<any>> {
    let params = new HttpParams();
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key as keyof Filter] !== null) {
          params = params.append(key, filter[key as keyof Filter].toString());
        }
      });
    }

    if (searchBy) {
      Object.keys(searchBy).forEach((key) => {
        if (searchBy[key as keyof any] !== null) {
          params = params.append(key, searchBy[key as keyof any].toString());
        }
      });
    }
    const response = await firstValueFrom(
      this.http.get<{ succeeded: boolean, data: Pagination<any> }>(`${this.url}/exclusive-promoter/proposals`, { params }));

    return response.data;
  }

  async getById(id: string): Promise<any> {
    const response = await firstValueFrom(
      this.http.get<{ succeeded: boolean, data: Pagination<any> }>(`${this.url}/exclusive-promoter/proposals/${id}`));

    return response.data;
  }

  async duplicateProposal(id: string): Promise<any> {
    const response = await firstValueFrom(
      this.http.post<{ succeeded: boolean, data: any }>(`${this.url}/exclusive-promoter/proposals/duplicate/${id}`, {})
    );
    return response.data;
  }

  private createFormDataFromProposal(proposal: any): FormData {
    const data = new FormData();
    data.append("dto", JSON.stringify(proposal));
    return data;
  }

  versionProposal(id: string): Promise<any> {
    return this.post(`${this.url}/exclusive-promoter/proposals/version/${id}`, {});
  }

  async calculate(payload: FormData): Promise<ExclusivePromoterProposal> {
    const response = await firstValueFrom(
      this.http.post<{ succeeded: boolean, data: ExclusivePromoterProposal }>(`${this.url}/exclusive-promoter/proposals/calculate`, payload));

    return response.data;
  }

  async getProposalReduced(id: string): Promise<ProposalOrderReduced> {
    const response = await firstValueFrom(
      this.http.get<{ succeeded: boolean, data: ProposalOrderReduced }>(
        `${this.url}/exclusive-promoter/proposals/${id}/reduced`));

    return response.data;
  }

  create(payload: any): Promise<void> {
    return this.post(`${this.url}/exclusive-promoter/proposals`, payload);
  }

  update(payload: any): Promise<void> {
    return this.put(`${this.url}/exclusive-promoter/proposals`, payload);
  }

  updateStatus(id: string, statusId: number): Promise<void> {
    return this.put(`${this.url}/exclusive-promoter/proposals/${id}/status?status=${statusId}`, {});
  }

  deleteProposal(id: string): Promise<void> {
    return this.delete(`${this.url}/exclusive-promoter/proposals/${id}`);
  }

  async saveProposalFile(proposalId: string, payload: FormData): Promise<any> {
    const response = await firstValueFrom(
      this.http.post<{
        succeeded: boolean;
        data: any;
      }>(`${this.url}/exclusive-promoter/proposals/${proposalId}/files`, payload)
    );
    return response.data;
  }

  async getProposalFileDocuments(
    proposalId: string,
    filter?: Filter
  ): Promise<Pagination<any>> {
    let params = new HttpParams();

    if (filter) {
      Object.keys(filter).forEach((key) => {
        params = params.append(key, filter[key as keyof Filter].toString());
      });
    }

    const response = await firstValueFrom(
      this.http.get<{
        succeeded: boolean;
        data: Pagination<any>;
      }>(`${this.url}/exclusive-promoter/proposals/${proposalId}/files`, { params })
    );
    return response.data;
  }

  async downloadFile(url: string): Promise<any> {
    let params = new HttpParams();
    let options = new HttpHeaders({
      Accept: 'application/pdf',
    });

    const response = await firstValueFrom(
      this.http.get(
        `${this.url}/exclusive-promoter/proposals/${url}/download`,
        {
          params,
          headers: options,
          responseType: 'blob',
        }
      )
    );
    return response;
  }

  exportFileExcel(proposalId: string): Promise<any> {
    return this.http.post(`${this.url}/exclusive-promoter/proposals/${proposalId}/excel/complete`, {}, { responseType: 'blob' }).toPromise();
  }
  exportFilePdfResume(proposalId: string): Promise<any> {
    return this.http.post(`${this.url}/exclusive-promoter/proposals/${proposalId}/pdf/reduced`, {}, { responseType: 'blob' }).toPromise();
  }
  exportFilePdfCompleted(proposalId: string): Promise<any> {
    return this.http.post(`${this.url}/exclusive-promoter/proposals/${proposalId}/pdf/complete`, {}, { responseType: 'blob' }).toPromise();
  }

  async exportExclusiveProposalFile(filter: Filter, searchBy?: any): Promise<any> {
    let params = new HttpParams();
    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (filter[key as keyof Filter] !== null) {
          params = params.append(key, filter[key as keyof Filter].toString());
        }
      });
    }

    if (searchBy) {
      Object.keys(searchBy).forEach((key) => {
        if (searchBy[key as keyof any] !== null) {
          params = params.append(key, searchBy[key as keyof any].toString());
        }
      });
    }

    const response = await firstValueFrom(
      this.http.get(`${this.url}/exclusive-promoter/proposals/report`, {
        params,
        responseType: 'blob' as 'json',
      })
    );

    return response;
  }

  sendEmail(proposalId: string, payload: any): Promise<void> {
    return this.post(`${this.url}/exclusive-promoter/proposals/${proposalId}/send-email`, payload);
  }
}